import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wrench, Check, Cog, Hammer, Gauge, Microscope, Paintbrush } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EquipmentListCardProps {
  equipment?: string[] | null;
  className?: string;
}

// Map equipment names to icons
const equipmentIcons: Record<string, React.ReactNode> = {
  'paint': <Paintbrush className="w-3 h-3" />,
  'ndt': <Microscope className="w-3 h-3" />,
  'test': <Gauge className="w-3 h-3" />,
  'hydraulic': <Cog className="w-3 h-3" />,
  'engine': <Cog className="w-3 h-3" />,
  'avionics': <Gauge className="w-3 h-3" />,
  'tool': <Hammer className="w-3 h-3" />,
};

const getEquipmentIcon = (equipment: string): React.ReactNode => {
  const lower = equipment.toLowerCase();
  for (const [key, icon] of Object.entries(equipmentIcons)) {
    if (lower.includes(key)) return icon;
  }
  return <Wrench className="w-3 h-3" />;
};

export const EquipmentListCard: React.FC<EquipmentListCardProps> = ({
  equipment,
  className
}) => {
  if (!equipment || equipment.length === 0) return null;

  return (
    <Card className={cn("border-border", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Hammer className="w-4 h-4 text-primary" />
          Facility Equipment
          <Badge variant="secondary" className="text-[10px] ml-auto">
            {equipment.length} item{equipment.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          {equipment.map((item, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg text-sm"
            >
              <div className="text-primary flex-shrink-0">
                {getEquipmentIcon(item)}
              </div>
              <span className="text-xs truncate">{item}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default EquipmentListCard;
